// Modal signature pad — full-screen on tablet, tap-in-and-sign flow.
// Uses signature_pad which handles Apple Pencil + finger cleanly on
// iPad Safari. Outputs a transparent-background PNG data-URL when the
// customer taps "Accept Signature".
import React, { useEffect, useRef } from "react";
import SignaturePad from "signature_pad";
import { X, Check, ArrowCounterClockwise } from "@phosphor-icons/react";

export default function SignaturePadModal({ open, onClose, onAccept, title = "Customer Signature" }) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  // Resize the canvas backing store to the device pixel ratio so the
  // stroke looks crisp on retina iPads. Doing this on every open
  // guarantees rotation works — we tear it down on close.
  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      padRef.current?.clear();
    };
    const pad = new SignaturePad(canvas, {
      backgroundColor: "rgba(255,255,255,0)",
      penColor: "#0f1622",
      minWidth: 0.7,
      maxWidth: 2.6,
      throttle: 8,
    });
    padRef.current = pad;
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("orientationchange", resize);
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("orientationchange", resize);
      pad.off();
      padRef.current = null;
    };
  }, [open]);

  if (!open) return null;

  const clear = () => padRef.current?.clear();
  const accept = () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) return;
    onAccept?.(pad.toDataURL("image/png"));
    onClose?.();
  };

  return (
    <div
      data-testid="signature-pad-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(1,35,64,0.94)",
        display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ padding: "20px 24px", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.32em", color: "#CFAB7A", textTransform: "uppercase" }}>ZMEX Signature</div>
          <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4 }}>{title}</div>
        </div>
        <button
          onClick={onClose}
          data-testid="signature-cancel"
          style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", padding: "10px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          <X size={14} weight="bold" /> Cancel
        </button>
      </div>

      <div style={{ flex: 1, padding: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 1100, aspectRatio: "16/6", background: "#fff", borderRadius: 8, boxShadow: "0 12px 40px rgba(0,0,0,0.4)", touchAction: "none" }}>
          <canvas ref={canvasRef} data-testid="signature-canvas" style={{ width: "100%", height: "100%", touchAction: "none" }} />
        </div>
      </div>

      <div style={{ padding: "16px 24px 28px", borderTop: "1px solid rgba(255,255,255,0.12)", display: "flex", gap: 12, justifyContent: "center" }}>
        <button
          onClick={clear}
          data-testid="signature-clear"
          style={{ padding: "16px 28px", borderRadius: 6, background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", fontSize: 14, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 10 }}
        >
          <ArrowCounterClockwise size={18} weight="bold" /> Clear
        </button>
        <button
          onClick={accept}
          data-testid="signature-accept"
          style={{ padding: "16px 32px", borderRadius: 6, background: "#CFAB7A", color: "#012340", border: "none", fontSize: 14, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 10 }}
        >
          <Check size={18} weight="bold" /> Accept Signature
        </button>
      </div>
    </div>
  );
}
